import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import products from './products';
import './App.css';

function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = products.find(p => p.id === id);
  const [mainIdx, setMainIdx] = useState(0);
  const [sliderStart, setSliderStart] = useState(0);
  const sliderVisibleCount = 4;

  const total = product ? product.screenshots.length : 0;
  const canSlideLeft = mainIdx > 0;
  const canSlideRight = mainIdx < total - 1;
  const mainImage = product && product.screenshots[mainIdx];

  // Move slider window so selector stays at the end of the visible range when possible
  useEffect(() => {
    if (!product) return;
    if (mainIdx < sliderVisibleCount) {
      setSliderStart(0);
    } else if (mainIdx > total - 1) {
      setSliderStart(total - sliderVisibleCount);
    } else if (mainIdx >= sliderStart + sliderVisibleCount) {
      setSliderStart(mainIdx - sliderVisibleCount + 1);
    } else if (mainIdx < sliderStart) {
      setSliderStart(mainIdx);
    }
  }, [mainIdx, total, sliderStart, product]);

  const handleThumbClick = idx => setMainIdx(idx);

  const handleSliderLeft = () => {
    if (canSlideLeft) {
      setMainIdx(mainIdx - 1);
    }
  };
  const handleSliderRight = () => {
    if (canSlideRight) {
      setMainIdx(mainIdx + 1);
    }
  };

  if (!product) return <div className="App"><h2>Product not found</h2></div>;

  // Custom content for NeuCraft Date Calendar
  const defaultDescriptions = {
    'date-calendar': {
      short: 'A comprehensive year-view calendar for quick date reference, holiday tracking, and date calculations.',
      full: (
        <>
          <p><b>NeuCraft Date Calendar</b> provides a clean, efficient interface for viewing an entire year at once. Ideal for those who need a straightforward date reference tool without the complexity of scheduling software.</p>
          <ul>
            <li>Full year calendar with customizable holiday tracking</li>
            <li>Multiple date calculation utilities</li>
            <li>Date series generation for recurring events</li>
            <li>Support for various date input formats</li>
          </ul>
          <p>Whether planning vacations, tracking holidays, or calculating important dates, this tool offers an intuitive solution that works entirely offline with no unnecessary features.</p>
          <b>Key Features:</b>
          <ul>
            <li>Complete Year View — See all months simultaneously for efficient planning</li>
            <li>Holiday Management — Track standard holidays and add custom dates</li>
            <li>Advanced Date Calculations — Add/subtract days, find days between dates, generate date series</li>
            <li>Visual Highlighting — Easily identify weekends, holidays, and today's date</li>
            <li>Simple Year Navigation — Quick access to past and future years</li>
            <li>Offline Security — Works without internet connection for complete privacy</li>
            <li>Clean Interface — Distraction-free design focused on functionality</li>
            <li>Automatic Settings Retention — Preferences stored locally for convenience</li>
          </ul>
          <b>Ideal For:</b>
          <ul>
            <li>Professionals needing quick date reference and planning tools</li>
            <li>Anyone tracking holidays and important dates</li>
            <li>Users who prefer simplicity and efficiency over complex interfaces</li>
            <li>Those concerned with privacy and offline functionality</li>
          </ul>
          <p>NeuCraft Date Calendar is designed as a reference and calculation tool, not a scheduling system—providing exactly what you need without unnecessary complications.</p>
          <b>Screenshots:</b>
          <ul>
            <li>Full-year calendar view with highlighted holidays and weekends</li>
            <li>The Holiday Editor showing customization options</li>
            <li>The Day Calculator performing date calculations</li>
            <li>The Settings panel allowing users to configure preferences</li>
          </ul>
          <b>App Features:</b>
          <ul>
            <li>Simple, full-year calendar view for quick date reference</li>
            <li>Track public holidays and add custom ones</li>
            <li>Built-in day calculator to determine past or future dates</li>
            <li>Minimalist interface without unnecessary complexity</li>
            <li>No internet connection required</li>
            <li>Automatic settings save for convenience</li>
          </ul>
          <b>System Requirements:</b>
          <ul>
            <li>Windows 10 or higher</li>
            <li>70 MB of free disk space</li>
            <li>512 MB RAM</li>
          </ul>
        </>
      )
    }
  };

  // Use custom description if product is Date Calendar
  const desc = product.id === 'date-calendar' && defaultDescriptions['date-calendar'] ? defaultDescriptions['date-calendar'] : { short: product.shortDesc, full: product.description };

  return (
    <div className="productpage-flex productpage-flex-darktext">
      <div className="productpage-mainimg-col">
        <div className="productpage-mainimg-frame productpage-mainimg-frame-nobg">
          <img
            src={mainImage}
            alt={product.name + ' screenshot'}
            className="productpage-mainimg"
          />
        </div>
        <div className="productpage-slider-row productpage-slider-row-tighter">
          <button
            className="slider-arrow"
            onClick={handleSliderLeft}
            disabled={!canSlideLeft}
            aria-label="Previous images"
            type="button"
          >
			<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			  <circle cx="12" cy="12" r="11" stroke="#e8bb5e" strokeWidth="2" fill="#3f6473" />
			  <polyline points="15,7 9,12 15,17" fill="none" stroke="#e8bb5e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
			</svg>
          </button>
          <div className="slider-thumbs">
            {product.screenshots.slice(sliderStart, sliderStart + sliderVisibleCount).map((img, idx) => (
              <img
                key={sliderStart + idx}
                src={img}
                alt={product.name + ' thumb'}
                className={mainIdx === sliderStart + idx ? 'slider-thumb selected' : 'slider-thumb'}
                onClick={() => handleThumbClick(sliderStart + idx)}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </div>
          <button
            className="slider-arrow"
            onClick={handleSliderRight}
            disabled={!canSlideRight}
            aria-label="Next images"
            type="button"
          >
			<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			  <circle cx="12" cy="12" r="11" stroke="#e8bb5e" strokeWidth="2" fill="#3f6473" />
			  <polyline points="9,7 15,12 9,17" fill="none" stroke="#e8bb5e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
			</svg>
          </button>
        </div>
      </div>
      <div className="productpage-details-col productpage-details-dark">
        <h1 className="productpage-title productpage-title-white">{product.name}</h1>
        <div className="productpage-desc productpage-desc-white" dangerouslySetInnerHTML={{ __html: desc.full || product.description }} />
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '32px 0 8px 0' }}>
          <span style={{ fontWeight: 'bold', fontSize: '1.3em', textAlign: 'center' }}>
            {product.price === 0 ? 'Price: Free' : `Price: $${product.price.toFixed(2)}`}
          </span>
        </div>
        <div className="productpage-actions productpage-actions-row">
          <button className="card-btn productpage-btn-small" onClick={() => navigate('/')} type="button" style={{cursor:'pointer'}}>&larr; Home</button>
          {product.price === 0 ? (
            <button
              className="card-btn productpage-btn-small"
              type="button"
              style={{cursor:'not-allowed', opacity:0.65, position:'relative'}}
              disabled
              title="This app is a gift to you."
            >
              Purchase
            </button>
          ) : (
            <a
              href={product.stripeLink}
              className="card-btn productpage-btn-small"
              target="_blank"
              rel="noopener noreferrer"
              style={{cursor:'pointer'}}
            >
              Purchase
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductPage;
